import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Textarea,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React from 'react';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';

import { GraphRef, graphRefToString } from 'src/app/graph/hooks/useGraphRef';
import { Form as CustomForm } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { UserPermission } from 'src/lib/graphqlTypes/types';
import { mergeQueryParams, setOverlayClosedLocation } from 'src/lib/routing';

import { CollectionPermissionsSection } from '../CollectionPermissionsSection';
import { useUpsertCollectionValidation } from '../sharedValidation';
import { useSetMinEditRole } from '../useUpdateEditRoles';

export const CreateCollectionModal = ({
  graphRef,
  isOpen,
  isShared,
}:
  | // Only collections tied to a graph can be shared
  { graphRef: GraphRef; isOpen: boolean; isShared: true }
  | { graphRef: GraphRef | null; isOpen: boolean; isShared: false }) => {
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    accountId: currentAccountId as string,
    skip: !currentAccountId,
  });

  const isEnterprise = currentPlan.currentPlan?.userRoles === true;

  const [createCollection] = useMutation<
    GraphQLTypes.CreateOperationCollectionMutation,
    GraphQLTypes.CreateOperationCollectionMutationVariables
  >(
    gql`
      mutation CreateOperationCollectionMutation(
        $collectionName: String!
        $description: String
        $variantRefs: [ID!]
        $isShared: Boolean!
        $isSandbox: Boolean!
      ) {
        createOperationCollection(
          name: $collectionName
          description: $description
          variantRefs: $variantRefs
          isShared: $isShared
          isSandbox: $isSandbox
        ) {
          ... on OperationCollection {
            id
            variants {
              id
              operationCollections {
                id
              }
            }
            createdBy @include(if: $isSandbox) {
              id
              ... on User {
                sandboxOperationCollections {
                  id
                }
              }
            }
          }
          ... on Error {
            message
          }
        }
      }
    `,
    {
      onError: (error) => setCreateCollectionErrorMessage(error.message),
    },
  );

  const [createCollectionErrorMessage, setCreateCollectionErrorMessage] =
    React.useState<string>();
  const { setMinEditRole } = useSetMinEditRole(setCreateCollectionErrorMessage);

  const trackCustomEvent = useTrackCustomEvent();

  const location = useLocation();
  const history = useHistory();
  const onClose = React.useCallback(
    (focusCollectionId?: string) => {
      const modalClosedLocation = setOverlayClosedLocation(location);
      if (focusCollectionId) {
        history.replace({
          ...modalClosedLocation,
          search: mergeQueryParams(modalClosedLocation.search, {
            focusCollectionId,
          }),
        });
      } else {
        history.replace(modalClosedLocation);
      }
    },
    [location, history],
  );

  const { validationSchema, loading } = useUpsertCollectionValidation(
    isShared ? { graphRef, isShared } : { graphRef, isShared },
  );

  const {
    errors,
    touched,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik<Yup.InferType<typeof validationSchema>>({
    initialValues: {
      name: '',
      description: '',
      minEditRole: null,
    },
    async onSubmit(formValues) {
      try {
        const printedGraphRef = graphRefToString(graphRef);
        const isSandbox = graphRef === null;
        const result = (
          await createCollection({
            variables: {
              collectionName: formValues.name,
              description: formValues.description,
              isShared,
              isSandbox,
              variantRefs: printedGraphRef ? [printedGraphRef] : [],
            },
          })
        ).data?.createOperationCollection;
        if (result?.__typename !== 'OperationCollection') {
          setCreateCollectionErrorMessage(
            result?.message ?? 'Failed to create collection',
          );
          return;
        }
        if (!isShared || values.minEditRole === null) {
          trackCustomEvent({
            action: 'create_operation_collection',
            category: 'Operation Collections',
            label: isSandbox ? 'sandbox' : 'personal',
          });
          onClose(result.id);
          return;
        }
        const updateEditRolesResult = (
          await setMinEditRole({
            collectionId: result.id,
            minEditRole: values.minEditRole,
          })
        ).data?.operationCollection?.setMinEditRole;
        if (updateEditRolesResult?.__typename === 'OperationCollection') {
          trackCustomEvent({
            action: 'create_operation_collection',
            category: 'Operation Collections',
            label: 'shared',
          });
          onClose(result.id);
        } else {
          setCreateCollectionErrorMessage(
            updateEditRolesResult?.message ??
              'Failed to set edit roles for collection',
          );
        }
      } catch {
        // Captured via `onError`
      }
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
  });

  return (
    <Modal isOpen={isOpen} onClose={() => onClose()} size="2xl">
      <ModalOverlay />
      <CustomForm onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              Create {isShared ? 'Shared' : 'Personal'} Collection
            </ModalTitle>
            <ModalDescription>
              {isShared ? (
                <>
                  A shared collection is visible to anyone who has access to{' '}
                  <span className="font-semibold">
                    {graphRef.graphId}@{graphRef.graphVariant}
                  </span>
                  .
                </>
              ) : (
                'A personal collection is only visible to you.'
              )}
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <Loading />
            ) : (
              <>
                <FormControl
                  isRequired
                  isInvalid={!!errors.name && !!touched.name}
                >
                  <FormLabel>Collection Name</FormLabel>
                  <Input
                    autoFocus
                    isDisabled={isSubmitting}
                    name="name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={`Your ${
                      isShared ? 'Shared' : 'Personal'
                    } Collection`}
                    value={values.name}
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={!!errors.description && !!touched.description}
                >
                  <FormLabel>Collection Description</FormLabel>
                  <Textarea
                    name="description"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description ?? undefined}
                    isDisabled={isSubmitting}
                  />
                  <FormErrorMessage>{errors.description}</FormErrorMessage>
                  <FormHelperText>
                    {values.description?.length ?? 0}/1000 characters used
                  </FormHelperText>
                </FormControl>
                {isShared && (
                  <CollectionPermissionsSection
                    graphRef={graphRef}
                    isEnterprise={isEnterprise}
                    minEditRole={values.minEditRole}
                    setMinEditRole={(minEditRole: UserPermission | null) => {
                      setFieldValue('minEditRole', minEditRole);
                    }}
                  />
                )}
                {createCollectionErrorMessage && (
                  <ErrorMessage>{createCollectionErrorMessage}</ErrorMessage>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                onClick={() => onClose()}
                type="button"
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Tooltip
                disabled={Object.entries(errors).length === 0}
                label={Object.values(errors).flatMap((error, i) => (
                  <>
                    {i !== 0 && <br />}
                    {error}
                  </>
                ))}
              >
                <Button
                  variant="primary"
                  isDisabled={loading || Object.entries(errors).length !== 0}
                  isLoading={isSubmitting}
                  loadingText="Add"
                  type="submit"
                >
                  Add
                </Button>
              </Tooltip>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </CustomForm>
    </Modal>
  );
};
